import { JSX, useEffect, useMemo, useState } from "react";
import "./ChainsVulnerability.css";
// @ts-ignore
import { api } from "../../../api/client.tsx";

type ChainsVulnerabilityProps = {
  imageId: string;
  l2ImageId?: string;
};

type ChainsRow = {
  image: string;
  name: string;
  version: string;
  cve: string;
  severity: string;
  layer: number | string;
  chains: string;
  link: string;
};

function normalizeSeverity(s: unknown): string {
  const v = String(s ?? "").trim();
  if (!v) return "";
  const lower = v.toLowerCase();
  if (lower.startsWith("negl")) return "Negligible";
  if (lower.startsWith("low")) return "Low";
  if (lower.startsWith("med")) return "Medium";
  if (lower.startsWith("high")) return "High";
  if (lower.startsWith("crit")) return "Critical";
  if (lower.startsWith("unk")) return "Unknown";
  return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
}

function rawTailFromImageId(id: string): string {
  const s = decodeURIComponent(String(id || "").trim());
  const us = s.indexOf("_");
  return us < 0 ? s : s.slice(us + 1);
}

function buildVersionKeyCandidates(idOrTail: string): string[] {
  const raw = rawTailFromImageId(idOrTail);
  const a = raw;
  const b = raw.replace(/_/g, "-");
  const c = raw.replace(/-/g, "_");
  return Array.from(new Set([a, b, c])).filter(Boolean);
}

const ITEMS_PER_PAGE = 15;

export default function ChainsVulnerability({
  imageId,
  l2ImageId,
}: ChainsVulnerabilityProps) {
  const isAll = imageId.toLowerCase() === "all";

  const [rows, setRows] = useState<ChainsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setRows([]);
    setCurrentPage(1);
    setError(null);
  }, [imageId, l2ImageId, isAll]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (isAll) {
        setRows([]);
        setLoading(false);
        return;
      }

      const effectiveImageId = (l2ImageId || imageId || "").trim();
      if (!effectiveImageId) {
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let data: any[] = [];
        const versionCandidates = buildVersionKeyCandidates(effectiveImageId);

        for (const key of versionCandidates) {
          try {
            const res = await api.level2.byVersionChained(key);
            if (Array.isArray(res) && res.length > 0) {
              data = res;
              break;
            }
          } catch {
          }
        }

        if (!data.length) {
          try {
            const fallback = await api.level2.byImage(effectiveImageId);
            if (Array.isArray(fallback)) {
              data = fallback;
            }
          } catch {
          }
        }

        const mapped: ChainsRow[] = (data ?? [])
          .map((v: any) => {
            const cve = v.Vulnerability_ID ?? v.vulnerability_id ?? "";
            const chainsRaw = v.Chains ?? v.chains ?? v.chained_with ?? null;

            let chainsList: string[] = [];

            if (Array.isArray(chainsRaw)) {
              chainsList = chainsRaw.map((x: any) => String(x).trim());
            } else if (typeof chainsRaw === "string") {
              const txt = chainsRaw.trim();

              if (txt.startsWith("[") && txt.endsWith("]")) {
                const inner = txt.slice(1, -1);
                chainsList = inner
                  .split(",")
                  .map((part) =>
                    part
                      .replace(/['"]/g, "")
                      .trim()
                  )
                  .filter(Boolean);
              } else if (txt.length > 0) {
                chainsList = [txt];
              }
            }

            if (!chainsList.length) {
              return null;
            }

            const chainsDisplay = chainsList.join("\n");

            return {
              image: v.Image ?? v.image ?? effectiveImageId,
              name: v.Package ?? v.package ?? "",
              version: v.Version_Installed ?? v.version_installed ?? "",
              cve,
              severity: normalizeSeverity(v.Severity ?? v.severity ?? ""),
              layer: v.Layer ?? v.layer ?? "",
              link: cve ? `https://nvd.nist.gov/vuln/detail/${cve}` : "#",
              chains: chainsDisplay,
            };
          })
          .filter((r): r is ChainsRow => r !== null);

        if (!cancelled) {
          setRows(mapped);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setRows([]);
          setLoading(false);
          setError("Failed to load chained vulnerabilities.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [imageId, l2ImageId, isAll]);

  const total = rows.length;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / ITEMS_PER_PAGE)),
    [total]
  );

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return rows.slice(start, end);
  }, [rows, currentPage]);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <div className="chains-container">
      <div className="chains-title-section">
        <h1 className="chains-title">
          Chained Vulnerabilities
          <p className="chains-subtitle">
            Showing only CVEs that participate in at least one chain for this
            image.
          </p>
        </h1>
      </div>

      <div className="chains-table-wrapper">
        <div className="chains-table-titlebar" role="heading" aria-level={2}></div>

        <div className="chains-table-scroll">
          <table className="chains-table">
            <thead>
              <tr>
                <th>CVE ID</th>
                <th>Vulnerable Package</th>
                <th>Version</th>
                <th>Layer</th>
                <th>Severity</th>
                <th>Chained With</th>
                <th>Link</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={7}
                    style={{ textAlign: "center", padding: "1rem" }}
                  >
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && error && (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      textAlign: "center",
                      padding: "1rem",
                      color: "#941C2F",
                    }}
                  >
                    {error}
                  </td>
                </tr>
              )}

              {!loading && !error && pagedRows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    style={{ textAlign: "center", padding: "1rem" }}
                  >
                    No chained vulnerabilities found for this image.
                  </td>
                </tr>
              )}

              {!loading &&
                !error &&
                pagedRows.map((item, idx) => (
                  <tr key={`${item.cve}-${idx}`}>
                    <td>{item.cve}</td>
                    <td>{item.name}</td>
                    <td>{item.version}</td>
                    <td>{item.layer}</td>
                    <td>{item.severity}</td>
                    <td className="chains-cell">{item.chains}</td>
                    <td>
                      {item.link && item.link !== "#" ? (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && !error && total > 0 && (
        <div className="chains-pagination">
          <button
            className="pagination-link"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ‹
          </button>

          {(() => {
            const elements: JSX.Element[] = [];
            const windowSize = 5;
            let startPage = Math.max(
              1,
              currentPage - Math.floor(windowSize / 2)
            );
            let endPage = startPage + windowSize - 1;

            if (endPage > totalPages) {
              endPage = totalPages;
              startPage = Math.max(1, endPage - windowSize + 1);
            }

            if (startPage > 1) {
              elements.push(
                <button
                  key={1}
                  className="pagination-link"
                  onClick={() => goToPage(1)}
                >
                  1
                </button>
              );
              if (startPage > 2) {
                elements.push(<span key="start-ellipsis">…</span>);
              }
            }

            for (let i = startPage; i <= endPage; i++) {
              elements.push(
                <button
                  key={i}
                  className={`pagination-link ${
                    currentPage === i ? "active" : ""
                  }`}
                  onClick={() => goToPage(i)}
                >
                  {i}
                </button>
              );
            }

            if (endPage < totalPages) {
              if (endPage < totalPages - 1) {
                elements.push(<span key="end-ellipsis">…</span>);
              }
              elements.push(
                <button
                  key={totalPages}
                  className="pagination-link"
                  onClick={() => goToPage(totalPages)}
                >
                  {totalPages}
                </button>
              );
            }

            return elements;
          })()}

          <button
            className="pagination-link"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
